import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  ChevronLeft,
  Truck,
  MapPin,
  CreditCard,
  Wallet,
  CheckCircle,
  Loader2,
  Package
} from 'lucide-react';

const PharmacyCheckoutPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const lang = i18n.language;

  const orderData = location.state || { cartItems: [], subtotal: 0, discount: 0, deliveryFee: 0, total: 0 };

  const [address, setAddress] = useState({
    street: '',
    building: '',
    floor: '',
    phone: '',
    notes: '',
  });
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);

  const handlePlaceOrder = async () => {
    if (!address.street || !address.phone) {
      toast.error(lang === 'ar' ? 'يرجى إكمال العنوان' : 'Please complete the address');
      return;
    }

    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    setLoading(false);
    setOrderPlaced(true);
  };

  if (orderPlaced) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="order-success">
        <Card className="dark:bg-slate-800 border-0 shadow-lg max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {lang === 'ar' ? 'تم الطلب بنجاح!' : 'Order Placed!'}
            </h2>
            <p className="text-slate-600 mb-2">
              {lang === 'ar' ? 'رقم الطلب:' : 'Order #'} <span className="font-semibold">PH{Date.now().toString().slice(-6)}</span>
            </p>
            <p className="text-slate-500 mb-6">
              {lang === 'ar' 
                ? 'سيتم التواصل معك قريباً لتأكيد الطلب'
                : 'We will contact you soon to confirm your order'
              }
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => navigate('/pharmacy/orders')}>
                <Package className="h-4 w-4 mr-2" />
                {lang === 'ar' ? 'تتبع الطلب' : 'Track Order'}
              </Button>
              <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => navigate('/pharmacy')}>
                {lang === 'ar' ? 'متابعة التسوق' : 'Continue Shopping'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="pharmacy-checkout">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">
              {lang === 'ar' ? 'إتمام الطلب' : 'Checkout'}
            </h1>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Checkout Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Delivery Address */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  {lang === 'ar' ? 'عنوان التوصيل' : 'Delivery Address'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{lang === 'ar' ? 'الشارع' : 'Street'} *</Label>
                  <Input 
                    value={address.street} 
                    onChange={(e) => setAddress({...address, street: e.target.value})}
                    placeholder={lang === 'ar' ? 'اسم الشارع' : 'Street name'}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{lang === 'ar' ? 'البناء' : 'Building'}</Label>
                    <Input 
                      value={address.building} 
                      onChange={(e) => setAddress({...address, building: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>{lang === 'ar' ? 'الطابق' : 'Floor'}</Label>
                    <Input 
                      value={address.floor} 
                      onChange={(e) => setAddress({...address, floor: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label>{lang === 'ar' ? 'رقم الهاتف' : 'Phone'} *</Label>
                  <Input 
                    value={address.phone} 
                    onChange={(e) => setAddress({...address, phone: e.target.value})}
                    placeholder="+963 xxx xxx xxx"
                  />
                </div>
                <div>
                  <Label>{lang === 'ar' ? 'ملاحظات' : 'Notes'}</Label>
                  <Textarea 
                    value={address.notes} 
                    onChange={(e) => setAddress({...address, notes: e.target.value})}
                    placeholder={lang === 'ar' ? 'ملاحظات إضافية للسائق...' : 'Additional notes for driver...'}
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Payment Method */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  {lang === 'ar' ? 'طريقة الدفع' : 'Payment Method'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3">
                  <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-colors cursor-pointer ${paymentMethod === 'cash' ? 'border-green-500 bg-green-50' : 'border-slate-200'}`}>
                    <RadioGroupItem value="cash" id="cash" />
                    <Label htmlFor="cash" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-3">
                        <Wallet className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="font-medium">{lang === 'ar' ? 'الدفع عند الاستلام' : 'Cash on Delivery'}</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'ادفع عند وصول الطلب' : 'Pay when your order arrives'}</p>
                        </div>
                      </div>
                    </Label>
                  </div>
                  <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-colors cursor-pointer ${paymentMethod === 'card' ? 'border-green-500 bg-green-50' : 'border-slate-200'}`}>
                    <RadioGroupItem value="card" id="card" />
                    <Label htmlFor="card" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-3">
                        <CreditCard className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="font-medium">{lang === 'ar' ? 'بطاقة ائتمان' : 'Credit Card'}</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'ادفع ببطاقة الائتمان' : 'Pay with credit card'}</p>
                        </div>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div>
            <Card className="dark:bg-slate-800 border-0 shadow-sm sticky top-6">
              <CardHeader>
                <CardTitle className="text-lg">
                  {lang === 'ar' ? 'ملخص الطلب' : 'Order Summary'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Items */}
                <div className="space-y-2">
                  {orderData.cartItems.map(item => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <span>{item.name} x{item.quantity}</span>
                      <span>${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <Separator />

                {/* Totals */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}</span>
                    <span>${orderData.subtotal.toFixed(2)}</span>
                  </div>
                  {orderData.discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>{lang === 'ar' ? 'الخصم' : 'Discount'}</span>
                      <span>-${orderData.discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'التوصيل' : 'Delivery'}</span>
                    <span>{orderData.deliveryFee === 0 ? (lang === 'ar' ? 'مجاني' : 'Free') : `$${orderData.deliveryFee}`}</span>
                  </div>
                </div>

                <Separator />

                <div className="flex justify-between font-bold text-lg">
                  <span>{lang === 'ar' ? 'الإجمالي' : 'Total'}</span>
                  <span className="text-green-600">${orderData.total.toFixed(2)}</span>
                </div>

                <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                  <Truck className="h-4 w-4" />
                  <span>{lang === 'ar' ? 'التوصيل خلال 30-45 دقيقة' : 'Delivery in 30-45 minutes'}</span>
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700" 
                  size="lg"
                  onClick={handlePlaceOrder}
                  disabled={loading}
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{lang === 'ar' ? 'جارٍ الطلب...' : 'Placing Order...'}</>
                  ) : (
                    <>{lang === 'ar' ? 'تأكيد الطلب' : 'Place Order'}</>
                  )}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PharmacyCheckoutPage;
